/* PUSSEL-LARM MANAGER — header button, scheduler sheet, polling for due alarms.
 * Polls server every 20s for alarms that have hit their ring_at.
 * When any due alarm is found, mounts PuzzleAlarmModal until solved. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlarmClock, Plus, Trash2, Play, Repeat, Clock } from 'lucide-react';
import PuzzleAlarmModal from './PuzzleAlarmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRing(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('sv-SE', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

function localISO(date) {
  // Build a local-time HH:MM input default = now+5min
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PuzzleAlarmsManager() {
  const [open, setOpen] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const [active, setActive] = useState(null); // alarm being rung
  const [label, setLabel] = useState('Pussel-larm');
  const [puzzleType, setPuzzleType] = useState('random');
  const [whenLocal, setWhenLocal] = useState(() => {
    const d = new Date(Date.now() + 5 * 60 * 1000);
    return localISO(d);
  });
  const [repeat, setRepeat] = useState(false);
  const [busy, setBusy] = useState(false);
  const dueRef = useRef(new Set()); // ids we've already rung this session

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/puzzle-alarms`, { withCredentials: true });
      setAlarms(r.data?.items || []);
    } catch {/**/}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 20s for due alarms
  useEffect(() => {
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/puzzle-alarms`, { withCredentials: true });
        const items = r.data?.items || [];
        setAlarms(items);
        if (active) return; // already ringing
        const now = Date.now();
        const due = items.find((a) => {
          const t = new Date(a.ring_at).getTime();
          return t <= now && !dueRef.current.has(a.id);
        });
        if (due) {
          dueRef.current.add(due.id);
          setActive(due);
        }
      } catch {/**/}
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [active]);

  const schedule = async () => {
    if (!whenLocal) { toast.error('Välj tid'); return; }
    setBusy(true);
    try {
      // Convert local datetime-local string to ISO with local timezone
      const d = new Date(whenLocal);
      await axios.post(`${API}/puzzle-alarms`, {
        label,
        ring_at: d.toISOString(),
        puzzle_type: puzzleType,
        repeat_daily: repeat,
      }, { withCredentials: true });
      toast.success('🚨 Larm schemalagt');
      await load();
      setOpen(false);
    } catch (e) {
      toast.error('Kunde inte spara larm');
    } finally { setBusy(false); }
  };

  const testNow = () => {
    setActive({ id: null, label: 'TEST · ' + label, puzzle_type: puzzleType });
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/puzzle-alarms/${id}`, { withCredentials: true });
      toast.success('Borttaget');
      load();
    } catch {/**/}
  };

  const ringNow = (a) => {
    dueRef.current.add(a.id);
    setActive(a);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="puzzle-alarm-trigger"
        className="px-3 py-1.5 rounded-full bg-gradient-to-br from-rose-200 to-pink-300 hover:from-rose-300 hover:to-pink-400 text-rose-900 text-xs font-bold shadow-sm flex items-center gap-1 border border-white/60"
        title="Pussel-larm"
      >
        🚨 Pussel-larm
      </button>

      {open && (
        <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-rose-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-rose-100 via-pink-100 to-amber-100 px-5 py-4 border-b border-rose-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-extrabold text-rose-800 flex items-center gap-2">
                  <AlarmClock className="w-5 h-5" /> Pussel-larm
                </h2>
                <button onClick={() => setOpen(false)} data-testid="alarm-sheet-close" className="text-rose-700 hover:text-rose-900 text-2xl leading-none">×</button>
              </div>
              <p className="text-xs text-rose-700/80 italic mt-1">Larm som BARA stängs av när du löser ett pussel ✨</p>
            </div>

            <div className="p-5 space-y-4">
              {/* NEW ALARM FORM */}
              <div className="bg-rose-50 rounded-2xl p-4 space-y-3 border border-rose-200">
                <div className="text-xs font-bold uppercase text-rose-700 flex items-center gap-1"><Plus className="w-3 h-3" /> Nytt larm</div>
                <div className="space-y-2">
                  <label className="text-xs text-gray-600 font-medium">Etikett</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    data-testid="alarm-label-input"
                    className="w-full px-3 py-2 rounded-xl border border-rose-200 focus:border-rose-500 outline-none text-sm"
                    placeholder="Pussel-larm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-gray-600 font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Tid</label>
                  <input
                    type="datetime-local"
                    value={whenLocal}
                    onChange={(e) => setWhenLocal(e.target.value)}
                    data-testid="alarm-time-input"
                    className="w-full px-3 py-2 rounded-xl border border-rose-200 focus:border-rose-500 outline-none text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-600 font-medium">Pusseltyp</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { id: 'random', label: '🎲 Slumpat' },
                      { id: 'math', label: '🧮 Matte' },
                      { id: 'digits', label: '🔢 Siffror' },
                      { id: 'sentence', label: '🔤 Skriv av' },
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setPuzzleType(t.id)}
                        data-testid={`alarm-puzzle-${t.id}`}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold ${puzzleType === t.id ? 'bg-rose-500 text-white' : 'bg-white border border-rose-200 text-rose-700'}`}
                      >{t.label}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} data-testid="alarm-repeat" />
                  <Repeat className="w-3 h-3" /> Upprepa dagligen
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={schedule}
                    disabled={busy}
                    data-testid="alarm-save"
                    className="flex-1 px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-sm disabled:opacity-50"
                  >Schemalägg</button>
                  <button
                    onClick={testNow}
                    data-testid="alarm-test-now"
                    className="px-4 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold text-sm flex items-center gap-1 border border-amber-300"
                  ><Play className="w-3 h-3" /> Testa nu</button>
                </div>
              </div>

              {/* SCHEDULED LIST */}
              <div className="space-y-2" data-testid="alarm-list">
                <div className="text-xs font-bold uppercase text-gray-600">Schemalagda ({alarms.length})</div>
                {alarms.length === 0 && (
                  <p className="text-xs text-gray-400 italic text-center py-3">Inga aktiva larm 💤</p>
                )}
                {alarms.map((a) => (
                  <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`alarm-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-800 truncate">{a.label}</div>
                      <div className="text-[11px] text-gray-500">
                        {fmtRing(a.ring_at)} · {a.puzzle_type}{a.repeat_daily ? ' · 🔁' : ''}{a.fail_count ? ` · 🚨 ${a.fail_count} fel` : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => ringNow(a)}
                      data-testid={`alarm-ring-${a.id}`}
                      className="text-xs px-2 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold border border-amber-200"
                      title="Ring nu"
                    ><Play className="w-3 h-3" /></button>
                    <button
                      onClick={() => remove(a.id)}
                      data-testid={`alarm-delete-${a.id}`}
                      className="text-xs px-2 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200"
                      title="Ta bort"
                    ><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {active && (
        <PuzzleAlarmModal
          alarm={active}
          onDismissed={() => { setActive(null); load(); }}
          onCancel={() => { setActive(null); load(); }}
        />
      )}
    </>
  );
}
